import { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useInspectionOrdersStore } from '../store/inspectionOrdersStore';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import type { InspectionOrder } from '../types/inspectionOrder';
import './InspectionsOverviewPage.css';

const STATE_FALLBACK_LABELS: Record<string, string> = {
  OPEN: 'Offen',
  IN_PROGRESS: 'In Arbeit',
  COMPLETED: 'Abgeschlossen',
  CANCELLED: 'Abgebrochen',
  PLANNED: 'Geplant'
};

const toModifier = (value: string | null | undefined) =>
  (value ?? 'unbekannt').toLowerCase().replace(/[^a-z0-9]+/g, '-');

const resolveStateLabel = (state: string | null, stateLabel: string | null) => {
  if (stateLabel && stateLabel.trim().length > 0) {
    return stateLabel.trim();
  }
  if (!state || state.trim().length === 0) {
    return 'Unbekannt';
  }
  const upper = state.toUpperCase();
  return STATE_FALLBACK_LABELS[upper] ?? state;
};

const formatStartTime = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

interface OrderViewModel {
  id: string;
  customerName: string;
  siteName: string;
  statusLabel: string;
  statusModifier: string;
  startTime: string;
}

const mapToViewModel = (order: InspectionOrder): OrderViewModel => ({
  id: order.id,
  customerName: order.customerName,
  siteName: order.siteName,
  statusLabel: resolveStateLabel(order.state ?? null, order.stateLabel ?? null),
  statusModifier: toModifier(order.state ?? 'unbekannt'),
  startTime: formatStartTime(order.scheduledStart)
});

export const InspectionsOverviewPage = () => {
  const { todaysOrders, isLoading, isRefreshing, error, hasLoadedOnce, loadTodaysOrders, refreshTodaysOrders } =
    useInspectionOrdersStore(
      (state) => ({
        todaysOrders: state.todaysOrders,
        isLoading: state.isLoading,
        isRefreshing: state.isRefreshing,
        error: state.error,
        hasLoadedOnce: state.hasLoadedOnce,
        loadTodaysOrders: state.loadTodaysOrders,
        refreshTodaysOrders: state.refreshTodaysOrders
      }),
      shallow
    );

  useEffect(() => {
    void loadTodaysOrders();
  }, [loadTodaysOrders]);

  const resolveScrollContainer = useCallback(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    return document.querySelector<HTMLElement>('.app-shell__content');
  }, []);

  const handleRefresh = useCallback(async () => {
    await refreshTodaysOrders();
  }, [refreshTodaysOrders]);

  usePullToRefresh({
    getTarget: resolveScrollContainer,
    onRefresh: handleRefresh,
    isEnabled: !isLoading && !isRefreshing
  });

  const orderViewModels = useMemo(() => todaysOrders.map(mapToViewModel), [todaysOrders]);
  const hasOrders = orderViewModels.length > 0;

  return (
    <section className="overview-page">
      <header className="overview-page__header">
        <h2>Inspektionsübersicht</h2>
        <p className="overview-page__subtitle">
          Starte mit deinen heutigen Aufträgen. Ziehe die Ansicht nach unten, um zu aktualisieren.
        </p>
      </header>

      <article className="overview-card" aria-live="polite">
        <div className="overview-card__title-row">
          <h3>Heutige Aufträge</h3>
          <button
            type="button"
            className="overview-card__refresh-button"
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
          >
            Erneut laden
          </button>
        </div>

        {isLoading ? (
          <div className="overview-card__loading" role="status">
            <span className="overview-card__spinner" aria-hidden="true" />
            <span>Lade Aufträge...</span>
          </div>
        ) : (
          <>
            {isRefreshing && (
              <div className="overview-card__info">Aktualisiere Aufträge …</div>
            )}

            {error && (
              <div className="overview-card__message overview-card__message--error" role="alert">
                <span>{error}</span>
                <button
                  type="button"
                  className="overview-card__retry"
                  onClick={handleRefresh}
                  disabled={isLoading || isRefreshing}
                >
                  Erneut laden
                </button>
              </div>
            )}

            {hasOrders && (
              <ul className="orders-list">
                {orderViewModels.map((order) => (
                  <li key={order.id}>
                    <Link
                      to={`/inspections/${order.id}`}
                      className="orders-list__item"
                      aria-label={`Auftrag ${order.customerName} öffnen`}
                    >
                      <div className="orders-list__meta">
                        <span className="orders-list__time">{order.startTime}</span>
                        <span
                          className={`orders-list__status orders-list__status--${order.statusModifier}`}
                        >
                          {order.statusLabel}
                        </span>
                      </div>
                      <div className="orders-list__title">{order.customerName}</div>
                      <div className="orders-list__details">{order.siteName}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {!hasOrders && !error && hasLoadedOnce && (
              <div className="overview-card__message overview-card__message--empty">
                Für heute sind keine Aufträge geplant.
              </div>
            )}
          </>
        )}
      </article>
    </section>
  );
};
